import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { updateWeightedAverageCost } from "@/lib/weighted-average-cost";
import { notificationSound } from "@/lib/notification-sound";
import { getErrorMessage, logError } from "@/lib/error-messages";

export interface Ingredient {
  id: string;
  restaurant_id: string;
  name: string;
  unit: string;
  current_stock: number;
  minimum_stock: number;
  cost_per_unit: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StockMovement {
  id: string;
  restaurant_id: string;
  ingredient_id: string;
  type: "entry" | "exit" | "adjustment" | "sale";
  quantity: number;
  previous_stock: number;
  new_stock: number;
  cost: number | null;
  reason: string | null;
  order_id: string | null;
  performed_by: string;
  created_at: string;
  ingredient?: Ingredient;
}

export function useIngredients() {
  const { activeRestaurant } = useRestaurantContext();
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logOperation } = useOperationLogs();

  const fetchIngredients = useCallback(async () => {
    if (!activeRestaurant) {
      setIngredients([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("ingredients" as any)
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("name");

      if (error) throw error;
      setIngredients((data as unknown as Ingredient[]) || []);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
      toast.error("Erro ao carregar ingredientes");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  useEffect(() => {
    fetchIngredients();
  }, [fetchIngredients]);

  const createIngredient = async (data: {
    name: string;
    unit: string;
    current_stock?: number;
    minimum_stock?: number;
    cost_per_unit?: number;
  }) => {
    if (!activeRestaurant) return null;

    try {
      const { data: newIngredient, error } = await supabase
        .from("ingredients" as any)
        .insert({
          restaurant_id: activeRestaurant.id,
          name: data.name,
          unit: data.unit,
          current_stock: data.current_stock || 0,
          minimum_stock: data.minimum_stock || 0,
          cost_per_unit: data.cost_per_unit || 0,
        })
        .select()
        .single();

      if (error) throw error;

      const ingredient = newIngredient as unknown as Ingredient;

      await logOperation("create", "ingredient" as any, ingredient.id, {
        name: ingredient.name,
        unit: ingredient.unit,
        initial_stock: ingredient.current_stock,
      });

      setIngredients((prev) => [...prev, ingredient].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Ingrediente criado com sucesso!");
      return ingredient;
    } catch (error) {
      logError(error, "useIngredients.createIngredient");
      toast.error(getErrorMessage(error, "criar", "ingredient"));
      return null;
    }
  };

  const updateIngredient = async (id: string, data: Partial<Ingredient>) => {
    const currentIngredient = ingredients.find(i => i.id === id);
    
    try {
      const { error } = await supabase
        .from("ingredients" as any)
        .update(data)
        .eq("id", id);

      if (error) throw error;

      await logOperation("update", "ingredient" as any, id, {
        name: data.name || currentIngredient?.name,
        changes: data,
      });

      setIngredients((prev) =>
        prev.map((ing) => (ing.id === id ? { ...ing, ...data } : ing))
      );
      toast.success("Ingrediente atualizado!");
      return true;
    } catch (error) {
      logError(error, "useIngredients.updateIngredient");
      toast.error(getErrorMessage(error, "atualizar", "ingredient"));
      return false;
    }
  };

  const deleteIngredient = async (id: string) => {
    const ingredient = ingredients.find(i => i.id === id);
    
    try {
      const { error } = await supabase
        .from("ingredients" as any)
        .delete()
        .eq("id", id);

      if (error) throw error;

      await logOperation("delete", "ingredient" as any, id, {
        name: ingredient?.name,
      });

      setIngredients((prev) => prev.filter((ing) => ing.id !== id));
      toast.success("Ingrediente removido!");
      return true;
    } catch (error) {
      logError(error, "useIngredients.deleteIngredient");
      toast.error(getErrorMessage(error, "excluir", "ingredient"));
      return false;
    }
  };

  const addStockMovement = async (
    ingredientId: string,
    type: "entry" | "exit" | "adjustment",
    quantity: number,
    reason?: string,
    cost?: number,
    expense?: {
      title: string;
      category_id: string;
      supplier_id?: string;
      due_date?: string;
    }
  ) => {
    if (!activeRestaurant) return false;

    try {
      const ingredient = ingredients.find((i) => i.id === ingredientId);
      if (!ingredient) throw new Error("Ingredient not found");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const previousStock = ingredient.current_stock;
      let newStock: number;

      if (type === "entry") {
        newStock = previousStock + quantity;
      } else if (type === "exit") {
        newStock = previousStock - quantity;
      } else {
        newStock = quantity; // adjustment sets the exact value
      }

      // Calculate unit cost for entry movements
      const movementQuantity = type === "adjustment" ? quantity - previousStock : quantity;
      const unitCost = type === "entry" && cost && quantity > 0 ? cost / quantity : 0;
      const totalCost = cost || 0;

      // Optional: create linked expense for entry movements with cost
      let expenseId: string | null = null;
      if (type === "entry" && expense && totalCost > 0) {
        const today = new Date().toISOString().slice(0, 10);
        const { data: createdExpense, error: expenseError } = await supabase
          .from("expenses" as any)
          .insert({
            restaurant_id: activeRestaurant.id,
            title: expense.title,
            description: reason || null,
            amount: totalCost,
            status: "open",
            expense_date: today,
            due_date: expense.due_date || null,
            category_id: expense.category_id,
            supplier_id: expense.supplier_id || null,
            origin: "stock_entry",
            created_by: user.id,
          })
          .select("id")
          .single();
        if (expenseError) throw expenseError;
        expenseId = (createdExpense as unknown as { id?: string } | null)?.id || null;
      }

      // Create movement record
      const { data: createdMovement, error: movementError } = await supabase
        .from("stock_movements" as any)
        .insert({
          restaurant_id: activeRestaurant.id,
          ingredient_id: ingredientId,
          type,
          quantity: movementQuantity,
          previous_stock: previousStock,
          new_stock: newStock,
          cost: cost || null,
          unit_cost: unitCost,
          total_cost: totalCost,
          reason: reason || null,
          performed_by: user.id,
          expense_id: expenseId,
        });

      if (movementError) throw movementError;

      // Back-link expense -> movement (optional)
      const movementId = (createdMovement as unknown as { id?: string } | null)?.id || null;
      if (expenseId && movementId) {
        await supabase
          .from("expenses" as any)
          .update({ linked_stock_movement_id: movementId })
          .eq("id", expenseId);
      }

      await logOperation("create", "stock_movement" as any, undefined, {
        ingredient_name: ingredient.name,
        type,
        quantity: movementQuantity,
        previous_stock: previousStock,
        new_stock: newStock,
        cost: totalCost,
        reason,
      });

      // Update weighted average cost for entry movements
      if (type === "entry" && unitCost > 0) {
        const wacResult = await updateWeightedAverageCost(ingredientId, quantity, unitCost);
        if (wacResult.success) {
          console.log(`Weighted average cost updated: ${wacResult.newCostPerUnit}`);
        }
      }

      // Update ingredient stock
      const { error: updateError } = await supabase
        .from("ingredients" as any)
        .update({ current_stock: newStock })
        .eq("id", ingredientId);

      if (updateError) throw updateError;

      // Update local state
      setIngredients((prev) =>
        prev.map((ing) =>
          ing.id === ingredientId ? { ...ing, current_stock: newStock } : ing
        )
      );

      // Check for low stock
      if (newStock <= ingredient.minimum_stock && newStock > 0) {
        notificationSound.playLowStock();
        toast.warning(`Estoque baixo: ${ingredient.name}`);
      } else if (newStock <= 0) {
        notificationSound.playLowStock();
        toast.error(`Estoque zerado: ${ingredient.name}`);
      } else {
        toast.success("Movimentação registrada!");
      }

      return true;
    } catch (error) {
      console.error("Error adding stock movement:", error);
      toast.error("Erro ao registrar movimentação");
      return false;
    }
  };

  const getLowStockIngredients = () => {
    return ingredients.filter(
      (ing) => ing.is_active && ing.current_stock <= ing.minimum_stock
    );
  };

  const getActiveIngredients = () => {
    return ingredients.filter((ing) => ing.is_active);
  };

  return {
    ingredients,
    isLoading,
    fetchIngredients,
    createIngredient,
    updateIngredient,
    deleteIngredient,
    addStockMovement,
    getLowStockIngredients,
    getActiveIngredients,
  };
}
